/* AI-driven deletion resolver — spans Exator's THREE item stores so "delete pizza"
 * finds a task, a schedule event, OR a calendar entry, not just tasks.
 *
 * Pure (no React/Firebase) → unit-testable in plain node. The App wires live state
 * in via a `stores` object and purges the returned {source,id} tuples from the right place.
 */

// Normalize for fuzzy matching: lowercase, strip punctuation, collapse whitespace.
export function normName(s) {
  return String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
}

// Does the user's `query` refer to this item `name`? Exact / substring either way,
// or majority word-overlap (words ≥3 chars) so "review finances" matches "finance review".
export function nameMatches(name, query) {
  const n = normName(name), q = normName(query);
  if (!n || !q) return false;
  if (n === q || n.includes(q) || q.includes(n)) return true;
  const nw = new Set(n.split(" "));
  const qw = q.split(" ").filter(w => w.length >= 3);
  if (!qw.length) return false;
  const hits = qw.filter(w => nw.has(w)).length;
  return hits >= Math.ceil(qw.length / 2);
}

// stores = {
//   tasks: [{id,name,icon,date,done}],
//   schedToday: [{id,name,category,linkedTaskId?}],   // today's schedEvents array
//   calToday: [{id,name,done}],                        // today's calEvents array
//   todayISO: "YYYY-MM-DD",
//   iconForCat: (category) => emoji,
// }
// Item shape returned: {source:"task"|"sched"|"cal", id, name, icon, done}
// Mirror schedEvents (linkedTaskId set) are skipped — the underlying task represents them,
// and purging the task already removes its mirror.
function T(t) { return { source: "task", id: t.id, name: t.name, icon: t.icon || "📝", done: !!t.done }; }
function S(ev, iconForCat) { return { source: "sched", id: ev.id, name: ev.name, icon: iconForCat(ev.category), done: false }; }
function C(ev) { return { source: "cal", id: ev.id, name: ev.name, icon: "📅", done: !!ev.done }; }

// Everything deletable, for name matching: all tasks (any date) + today's events.
export function collectAll(stores) {
  const { tasks, schedToday, calToday, iconForCat } = stores;
  const items = [];
  (tasks || []).forEach(t => items.push(T(t)));
  (schedToday || []).filter(ev => !ev.linkedTaskId).forEach(ev => items.push(S(ev, iconForCat)));
  (calToday || []).forEach(ev => items.push(C(ev)));
  return items;
}

// Scope-based set.
export function collectScope(stores, scope) {
  const { tasks, schedToday, calToday, todayISO, iconForCat } = stores;
  const items = [];
  const sched = (schedToday || []).filter(ev => !ev.linkedTaskId);
  if (scope === "today") {
    // Today's OPEN items — never already-done tasks (those live in Task history; clearing the
    // day shouldn't delete completed history). Schedule events have no done concept.
    (tasks || []).filter(t => (!t.date || t.date === todayISO) && !t.done).forEach(t => items.push(T(t)));
    sched.forEach(ev => items.push(S(ev, iconForCat)));
    (calToday || []).filter(ev => !ev.done).forEach(ev => items.push(C(ev)));
  } else if (scope === "all-open") {
    (tasks || []).filter(t => !t.done).forEach(t => items.push(T(t)));
    sched.forEach(ev => items.push(S(ev, iconForCat)));
    (calToday || []).filter(ev => !ev.done).forEach(ev => items.push(C(ev)));
  } else if (scope === "completed") {
    (tasks || []).filter(t => t.done).forEach(t => items.push(T(t)));
    (calToday || []).filter(ev => ev.done).forEach(ev => items.push(C(ev)));
  }
  return items;
}

// Name-based set. Each provided name is also split on "and"/","/"&"/"+", so a single
// "pizza and yoga" reference resolves to BOTH items even if the model didn't split it.
export function resolveByNames(stores, names) {
  const all = collectAll(stores);
  const parts = [];
  (names || []).forEach(nm => String(nm || "").split(/\s+and\s+|,|&|\+/i).forEach(p => {
    const s = p.trim(); if (s) parts.push(s);
  }));
  const picked = [], seen = new Set();
  parts.forEach(nm => {
    const hit = all.find(it => !seen.has(it.source + it.id) && nameMatches(it.name, nm));
    if (hit) { seen.add(hit.source + hit.id); picked.push(hit); }
  });
  return picked;
}

// Unified entry. Pass {names:[...]} (from deleteTask taskName or deleteTasks scope=named)
// or {scope:"today"|"all-open"|"completed"}. Always dedups by source+id.
export function resolveDeletables(stores, { scope, names } = {}) {
  const items = (names && names.length) ? resolveByNames(stores, names)
    : scope ? collectScope(stores, scope) : [];
  const seen = new Set();
  return items.filter(it => {
    const k = it.source + it.id;
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
}
